import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AddLocationAltRounded, AssignmentTurnedInRounded, BusinessRounded, CallRounded,
  CheckCircleRounded, ChevronRightRounded, CloseRounded, FilterAltRounded,
  LocationOnRounded, MapRounded, MyLocationRounded, OpenInNewRounded, PersonPinCircleRounded,
  SearchRounded, StarRounded, StorefrontRounded, TodayRounded, TrendingUpRounded,
  VisibilityRounded, WorkspacesRounded
} from "@mui/icons-material";
import "./FDCTargets.css";
import useFDCData, { FDCDataState } from "../services/useFDCData";
import { fdcRequest } from "../services/fdcApi";
import { useEffect } from "react";

const statusMeta = {
  available: { label: "Available", tone: "blue" },
  assigned: { label: "Assigned", tone: "violet" },
  contacted: { label: "Contacted", tone: "amber" },
  follow_up: { label: "Follow-up", tone: "amber" },
  interested: { label: "Interested", tone: "green" },
  registered: { label: "Registered", tone: "green" },
  rejected: { label: "Not interested", tone: "red" },
};

export default function FDCTargets({ staffId, onNavigate }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const resource=useFDCData(staffId,"targets");
  const targets=(resource.data || []).map(t=>({...t,id:t.targetId || String(t._id),assignedToMe:t.staffId===staffId}));
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [state, setState] = useState("all");
  const [selected, setSelected] = useState(null);
  const tab = searchParams.get("targetView") || "marketplace";

  const setTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", "targets"); next.set("targetView", nextTab);
    setSearchParams(next);
  };

  const categories = useMemo(() => [...new Set(targets.map(x => x.category))], [targets]);
  const states = useMemo(() => [...new Set(targets.map(x => x.state))], [targets]);
  const assigned = targets.filter(x => x.assignedToMe);
  const registered = assigned.filter(x => x.status === "registered");
  const followups = assigned.filter(x => ["contacted", "follow_up", "interested"].includes(x.status));

  const visible = useMemo(() => targets.filter((target) => {
    if (tab === "mine" && !target.assignedToMe) return false;
    if (tab === "followups" && !(target.assignedToMe && ["contacted", "follow_up", "interested"].includes(target.status))) return false;
    if (tab === "completed" && !(target.assignedToMe && ["registered", "rejected"].includes(target.status))) return false;
    if (category !== "all" && target.category !== category) return false;
    if (state !== "all" && target.state !== state) return false;
    const q = search.trim().toLowerCase();
    return !q || [target.name, target.category, target.address, target.city, target.state].some(v => String(v).toLowerCase().includes(q));
  }), [targets, tab, category, state, search]);

  const saveTarget=async(id,status)=>{
    try{const response=await fdcRequest(staffId,`targets/${encodeURIComponent(id)}`,"patch",{status});resource.setData(rows=>rows.map(t=>t.targetId===id?response.data:t));setSelected({...response.data,id:response.data.targetId,assignedToMe:true});}
    catch(e){resource.setError(e.response?.data?.message || e.message);}
  };
  const claimTarget=id=>saveTarget(id,"assigned");
  const updateStatus=(id,status)=>saveTarget(id,status);
  useEffect(()=>{const id=searchParams.get("target");if(id){const item=(resource.data || []).find(t=>t.targetId===id);if(item)setSelected({...item,id:item.targetId,assignedToMe:item.staffId===staffId})}},[searchParams,resource.data,staffId]);

  return <section className="fdcTargets"><FDCDataState {...resource}/>
    <div className="targetHero">
      <div>
        <span className="targetEyebrow"><PersonPinCircleRounded/> FIELD DEVELOPMENT</span>
        <h1>Targets & Market Opportunities</h1>
        <p>Discover assigned businesses, take ownership of opportunities, track visits and move prospects from first contact to onboarding.</p>
      </div>
      <button className="targetPrimary" onClick={() => onNavigate?.("onboarding")}><AddLocationAltRounded/> Onboard a client</button>
    </div>

    <div className="targetKpis">
      <article><span className="kpiIcon blue"><WorkspacesRounded/></span><div><small>Available opportunities</small><strong>{targets.filter(x=>!x.assignedToMe).length}</strong><p>Across target directory</p></div></article>
      <article><span className="kpiIcon violet"><AssignmentTurnedInRounded/></span><div><small>My assigned targets</small><strong>{assigned.length}</strong><p>Your active pipeline</p></div></article>
      <article><span className="kpiIcon amber"><TodayRounded/></span><div><small>Needs follow-up</small><strong>{followups.length}</strong><p>Continue conversations</p></div></article>
      <article><span className="kpiIcon green"><TrendingUpRounded/></span><div><small>Converted</small><strong>{registered.length}</strong><p>Registered clients</p></div></article>
    </div>

    <div className="targetPanel">
      <div className="targetTabs">
        {[['marketplace','Target marketplace'],['mine','My targets'],['followups','Follow-ups'],['completed','Completed']].map(([key,label]) =>
          <button key={key} className={tab===key?'active':''} onClick={()=>setTab(key)}>{label}{key==='mine' && <em>{assigned.length}</em>}</button>)}
      </div>
      <div className="targetToolbar">
        <label className="targetSearch"><SearchRounded/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search business, address, city or category..."/></label>
        <div className="targetFilters"><FilterAltRounded/>
          <select value={state} onChange={e=>setState(e.target.value)}><option value="all">All states</option>{states.map(x=><option key={x}>{x}</option>)}</select>
          <select value={category} onChange={e=>setCategory(e.target.value)}><option value="all">All categories</option>{categories.map(x=><option key={x}>{x}</option>)}</select>
        </div>
      </div>
      <div className="targetResultMeta"><div><strong>{visible.length}</strong> businesses</div><span>Your database-backed target pipeline</span></div>

      <div className="targetGrid">
        {visible.map(target => { const meta=statusMeta[target.status] || statusMeta.available; return <article className="targetCard" key={target.id}>
          <div className="targetCardTop"><span className="targetBusinessIcon"><StorefrontRounded/></span><span className={`targetStatus ${meta.tone}`}>{meta.label}</span></div>
          <div className="targetCardBody"><small>{target.category}</small><h3>{target.name}</h3><p className="targetAddress"><LocationOnRounded/>{target.address}, {target.city}, {target.state}</p>
            <div className="targetSignals"><span><StarRounded/>{target.rating || '—'} <small>({target.reviews} reviews)</small></span><span><BusinessRounded/>{target.state}</span></div>
          </div>
          <div className="targetCardActions">
            <button className="targetGhost" onClick={()=>setSelected(target)}><VisibilityRounded/> Details</button>
            {!target.assignedToMe ? <button className="targetClaim" onClick={()=>claimTarget(target.id)}>Assign to me <ChevronRightRounded/></button> : <button className="targetClaim" onClick={()=>setSelected(target)}>Update target <ChevronRightRounded/></button>}
          </div>
        </article>})}
      </div>
      {!visible.length && <div className="targetEmpty"><MyLocationRounded/><h3>No targets match this view</h3><p>Change the filters or return to the marketplace to discover more opportunities.</p><button onClick={()=>{setSearch('');setCategory('all');setState('all');setTab('marketplace')}}>Reset filters</button></div>}
    </div>

    {selected && <div className="targetDrawerBackdrop" onMouseDown={()=>setSelected(null)}><aside className="targetDrawer" onMouseDown={e=>e.stopPropagation()}>
      <header><div><span>{selected.category}</span><h2>{selected.name}</h2></div><button onClick={()=>setSelected(null)}><CloseRounded/></button></header>
      <div className="drawerLocation"><span><LocationOnRounded/></span><div><small>BUSINESS LOCATION</small><strong>{selected.address}</strong><p>{selected.city}, {selected.state}, Nigeria</p></div></div>
      <div className="drawerMetrics"><div><small>Google rating</small><strong><StarRounded/>{selected.rating || '—'}</strong><span>{selected.reviews} reviews</span></div><div><small>Pipeline status</small><strong>{(statusMeta[selected.status]||statusMeta.available).label}</strong><span>{selected.assignedToMe?'Assigned to you':'Unassigned'}</span></div></div>
      <section className="drawerSection"><h4>Contact & navigation</h4>
        <div className="drawerQuick">
          {selected.phone ? <a href={`tel:${selected.phone}`}><CallRounded/> Call business</a> : <button disabled><CallRounded/> No phone listed</button>}
          {selected.placeUrl ? <a href={selected.placeUrl} target="_blank" rel="noreferrer"><MapRounded/> Open in Maps <OpenInNewRounded/></a> : null}
        </div>
      </section>
      <section className="drawerSection"><h4>Marketing progress</h4><p>Update this opportunity as you contact, visit and qualify the business.</p>
        <div className="statusChoices">{[['assigned','Assigned'],['contacted','Contacted'],['follow_up','Follow-up'],['interested','Interested'],['visited','Visited'],['rejected','Not interested']].map(([key,label])=><button key={key} className={selected.status===key?'active':''} onClick={()=>updateStatus(selected.id,key)}><CheckCircleRounded/>{label}</button>)}</div>
      </section>
      <section className="drawerSection targetSource"><small>TARGET SOURCE</small><p>{selected.source}</p><small>Category dataset: {selected.slug}</small></section>
      <footer>{!selected.assignedToMe ? <button className="drawerMain" onClick={()=>claimTarget(selected.id)}>Assign this target to me</button> : <button className="drawerMain" disabled={selected.status==='registered'} onClick={()=>onNavigate?.('onboarding',{target:selected.id})}>Start client onboarding</button>}</footer>
    </aside></div>}
  </section>;
}
